/**
 * Display-side formatting for MCP tool results.
 *
 * On the wire, tool results are compact JSON — token-optimized for the model
 * (no indentation; large row sets in the columnar form produced by
 * apps/mcp-server/src/tools/compactRows.ts). Humans reading the tool dialog
 * get the friendly view instead: JSON is pretty-printed, and compacted
 * columnar tables are expanded back into plain row objects. The decoder here
 * mirrors `expandRows` on the server side and must stay in sync with that
 * format; it is display-only and never feeds back into the conversation.
 */

/** Cell sentinel of the compact format: key absent in this row. */
const ABSENT = '␀'

interface CompactedRows {
  format: string
  keys: Array<string>
  rows: Array<Array<unknown>>
  constants?: Record<string, unknown>
  dicts?: Record<string, Array<string>>
  flattened?: boolean
}

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isStringArray = (value: unknown): value is Array<string> =>
  Array.isArray(value) && value.every((v) => typeof v === 'string')

const isCompactedRows = (value: unknown): value is CompactedRows =>
  isPlainRecord(value) &&
  typeof value.format === 'string' &&
  value.format.startsWith('columnar') &&
  isStringArray(value.keys) &&
  Array.isArray(value.rows) &&
  value.rows.every((row) => Array.isArray(row))

const unflatten = (flat: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {}
  for (const [path, value] of Object.entries(flat)) {
    const segments = path.split('.')
    let node = out
    for (const segment of segments.slice(0, -1)) {
      const next = node[segment]
      if (isPlainRecord(next)) {
        node = next
      } else {
        const created: Record<string, unknown> = {}
        node[segment] = created
        node = created
      }
    }
    node[segments[segments.length - 1]] = value
  }
  return out
}

const expandRows = (compact: CompactedRows): Array<Record<string, unknown>> =>
  compact.rows.map((row) => {
    const flat: Record<string, unknown> = {}
    compact.keys.forEach((key, i) => {
      const dict = compact.dicts?.[key]
      const cell = row[i]
      const value =
        dict !== undefined && typeof cell === 'number' ? dict[cell] : cell
      if (value !== ABSENT) flat[key] = value
    })
    for (const [key, value] of Object.entries(compact.constants ?? {})) {
      if (value !== ABSENT) flat[key] = value
    }
    return compact.flattened === true ? unflatten(flat) : flat
  })

/**
 * Renders a tool-result string for the dialog: JSON gets pretty-printed, and
 * compacted columnar payloads (top-level fields like `results`/`data`) are
 * expanded back into readable row objects. Non-JSON input (e.g. the truncated
 * live-streaming preview or plain error text) is returned unchanged.
 */
export const formatToolResult = (text: string): string => {
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return text
  }
  if (isPlainRecord(parsed)) {
    for (const [key, value] of Object.entries(parsed)) {
      if (isCompactedRows(value)) {
        try {
          parsed[key] = expandRows(value)
        } catch {
          // Malformed compact payload — keep the original value visible.
        }
      }
    }
  }
  return JSON.stringify(parsed, null, 2)
}
